/**
 * Persistence utility for SDK context
 * 
 * Handles saving, loading, and clearing user/account context to localStorage.
 * SSR-safe (no-op when window is undefined).
 */

export interface PersistedContext {
  userId?: string;
  userTraits?: Record<string, any>;
  accountId?: string;
  accountTraits?: Record<string, any>;
  anonymousId: string;
}

const STORAGE_KEY = 'firstdistro_context';

/**
 * Save SDK context to localStorage
 * SSR-safe: no-op if window is undefined
 */
export function saveContext(context: PersistedContext): void {
  if (typeof window === 'undefined') return; // SSR-safe

  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(context));
  } catch (error) {
    // Handle storage quota errors gracefully
    if (error instanceof DOMException && error.name === 'QuotaExceededError') {
      console.warn('[FirstDistro] localStorage quota exceeded, context not saved');
    } else {
      console.warn('[FirstDistro] Failed to save context:', error);
    }
  }
}

/**
 * Load SDK context from localStorage
 * SSR-safe: returns null if window is undefined
 */
export function loadContext(): PersistedContext | null {
  if (typeof window === 'undefined') return null; // SSR-safe

  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return null;

    const context = JSON.parse(stored) as PersistedContext;
    
    // Validate structure
    if (typeof context !== 'object' || context === null) {
      return null;
    }
    
    // Ensure anonymousId exists (required)
    if (!context.anonymousId || typeof context.anonymousId !== 'string') {
      return null;
    }

    return context;
  } catch (error) {
    // Invalid JSON or other error
    console.warn('[FirstDistro] Failed to load context:', error);
    return null;
  }
}

/**
 * Clear SDK context from localStorage
 * SSR-safe: no-op if window is undefined
 */
export function clearContext(): void {
  if (typeof window === 'undefined') return; // SSR-safe

  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.warn('[FirstDistro] Failed to clear context:', error);
  }
}

